import { Button, Grid, makeStyles } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import AddBoxIcon from '@material-ui/icons/AddBox';
import MuiAlert from '@material-ui/lab/Alert';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable, { Row } from '../../../components/ui/blocks/DataTable';
import PageHeader from '../../../components/ui/blocks/PageHeader';
import { AlertContext } from '../../../components/contexts/WithAlertContext';
import CreateNewWebhook from './CreateNewWebhookDialog';
import DeleteWebhookDialog from './DeleteWebhookDialog';
import EditWebhook from './EditWebhookDialog';
import SigningSecretDialog from './SigningSecretDialog';
import WebhookService from '../../../services/WebhookService';
import { NewWebhook, Webhook, WebhookResponse } from '../../../types/Webhook/Webhook';
import { Action } from '../../../components/ui/blocks/ActionMenu';

function Alert(props: any) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const useStyles = makeStyles((theme) => ({
  main: {
    padding: theme.spacing(1),
  },
  mode: {
    width: 'fit-content',
    padding: '1px 8px',
    borderRadius: '4px',
  },
  live: {
    backgroundColor: theme.palette.success.main,
  },
  disabled: {
    backgroundColor: '#ffc931',
  },
}));

export default function WebHooksView() {
  const [webhooks, setWebhooks] = useState<WebhookResponse[]>([]);
  const [showCreateNewWebhook, setShowCreateNewWebhook] = useState(false);
  const [showSigningSecretDialog, setShowSigningSecretDialog] = useState(false);
  const [signingSecret, setSigningSecret] = useState<string>();
  const [showEditWebhook, setShowEditWebhook] = useState<NewWebhook>();
  const [showDeleteWebhook, setShowDeleteWebhook] = useState<NewWebhook>();

  const { t } = useTranslation('developer');
  const classes = useStyles();
  const alerting = useContext(AlertContext);

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = () => {
    WebhookService.getWebhooks().then((webhooks) => {
      setWebhooks(webhooks);
    });
  };

  const onGetWebhooks = () => {
    setShowCreateNewWebhook(false);
    setShowSigningSecretDialog(true);
    loadWebhooks();
  };

  const handleDeleteWebhook = (row: Webhook) => {
    WebhookService.deleteWebhook(row.id).then((res) => {
      if (res) {
        alerting.sendAlert('success', t('successfully deleted'));
        loadWebhooks();
      } else {
        alerting.sendAlert('error', t('unsuccessfully deleted'));
        loadWebhooks();
      }
      setShowDeleteWebhook(undefined);
    });
  };

  const enableOrDisable = (action: { id: string }, row: WebhookResponse) => {
    const values: NewWebhook = {
      ...row,
      active: !row.active,
      url: row.url,
      subscriptions: row.subscriptions.map(subscription => `${subscription.eventType}${subscription.legacy ? '.legacy' : ''}`),
      name: row.name,
    };
    WebhookService.updateWebhook(row.id, values).then(() => {
      onEdited();
    });
  };

  const onMenuClick = (action: Action, row: Row) => {
    const webhookResponse = webhooks.filter((w) => w.id === row.id)[0];
    const newWebhook = { ...webhookResponse, subscriptions: webhookResponse.subscriptions.map(subscription => `${subscription.eventType}${subscription.legacy ? '.legacy' : ''}`) };
    switch (action.id) {
      case 'edit':
        setShowEditWebhook(newWebhook);
        break;
      case 'show signing secret':
        setShowSigningSecretDialog(true);
        setSigningSecret(row.secret as string);
        break;
      case 'delete':
        setShowDeleteWebhook(newWebhook);
        break;
      case 'enable':
      case 'disable':
        enableOrDisable(action, webhooks.filter((w) => w.id === row.id)[0]);
        break;

      default:
    }
  };

  const filterActions = (row: Row, actions: Action[]) => {
    return actions.filter((action) => (action.id !== 'disable' && row.active === 'false') || (action.id !== 'enable' && row.active === 'true'));
  };

  const onEdited = () => {
    alerting.sendAlert('success', t('webhook edited'));
    setShowEditWebhook(undefined);
    loadWebhooks();
  };

  return (
    <React.Fragment>
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
        <PageHeader title={t('webhooks')} />
        <Button variant="contained" color="primary" startIcon={<AddBoxIcon />} onClick={() => setShowCreateNewWebhook(true)}>
          {t('new endpoint')}
        </Button>
      </Grid>
      {webhooks.length !== 0 ? (
        <>
          <DataTable
            columns={[
              {
                label: t('url'),
                id: 'url',
              },
              {
                label: t('name'),
                id: 'name',
              },
              {
                label: t('events'),
                id: 'subscriptions',
              },
              {
                label: t('mode'),
                id: 'active',
              },
            ]}
            rows={(() => {
              return webhooks.map((w) => ({
                ...w,
                active: w.active + '',
                subscriptions: w.subscriptions.map((s) => `${s.eventType}${s.legacy ? '.legacy' : ''}`),
              }));
            })()}
            showPagination={false}
            cellTransformations={{
              url: (url) => `${url}`,
              name: (name) => `${name}`,
              subscriptions: (events) => (
                <>
                  {(events as string[]).map((event) => {
                    return <Chip label={t(event)} />;
                  })}
                </>
              ),
              active: (mode) => (
                <Grid item className={`${classes.mode} ${mode === 'true' ? classes.live : classes.disabled}`}>
                  {`${mode === 'true' ? t('live') : t('disabled')}`}
                </Grid>
              ),
            }}
            actions={[
              {
                id: 'edit',
                label: t('edit'),
              },
              {
                id: 'show signing secret',
                label: t('show signing secret'),
              },
              {
                id: 'enable',
                label: t('enable'),
              },
              {
                id: 'disable',
                label: t('disable'),
              },
              {
                id: 'delete',
                label: t('delete'),
              },
            ]}
            filterActionsPerRow={filterActions}
            onAction={onMenuClick}
          />
        </>
      ) : (
        <Grid item xs={12} className={classes.main}>
          <Alert severity="warning">{t('no webhooks yet')}</Alert>
        </Grid>
      )}
      {showCreateNewWebhook && (
        <CreateNewWebhook
          isOpen={showCreateNewWebhook}
          onCancel={() => setShowCreateNewWebhook(false)}
          onSuccess={onGetWebhooks}
          setSigningSecret={setSigningSecret}
        />
      )}
      {showSigningSecretDialog && (
        <SigningSecretDialog isOpen={showSigningSecretDialog} onCancel={() => setShowSigningSecretDialog(false)} secretKey={signingSecret} />
      )}
      {showEditWebhook && <EditWebhook webhookRow={showEditWebhook} onClose={() => setShowEditWebhook(undefined)} onEdit={onEdited} open={true} />}
      {showDeleteWebhook && (
        <DeleteWebhookDialog webhook={showDeleteWebhook} onClose={() => setShowDeleteWebhook(undefined)} onDelete={handleDeleteWebhook} open={true} />
      )}
    </React.Fragment>
  );
}
